import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { useControlledState } from '@/hooks/useControlledState';

import Button, { ButtonElement } from '../button';

export interface ToggleProps {
  options: string[];
  selectedIndex?: number;
  onValueChange?: (index: number) => void;
  isHueInfused?: boolean;
  themeSwitchMode?: 'normal' | 'alternate';
  size?: 's' | 'm';
  responsive?: boolean;
  classNames?: {
    root?: string;
    tab?: string;
    selectedTab?: string;
  };
}

export const NewToggle = ({
  selectedIndex: propSelectedIndex,
  onValueChange,
  options,
  isHueInfused,
  size = 'm',
  responsive = true,
  themeSwitchMode = 'alternate',
}: ToggleProps) => {
  const [selectedIndex, setSelectedIndex] = useControlledState({
    prop: propSelectedIndex ?? undefined,
    defaultProp: propSelectedIndex ?? 0,
    onChange: onValueChange,
  });

  const rootClasses = cn(
    'flex rounded-3xl p-px gap-x-[2px] gap-y-5 border-transparent border md:border-neutral-N04',
    responsive &&
      'flex-col md:flex-row md:border md:bg-neutral-N00 md:hover:bg-neutral-N04',
    !responsive &&
      'flex-row border border-neutral-N04 bg-neutral-N00 hover:bg-neutral-N04'
  );

  return (
    <div>
      <ul
        role='tablist'
        aria-orientation='horizontal'
        dir='ltr'
        className={rootClasses}
      >
        {options.map((option, i) => (
          <ToggleItem
            key={option}
            value={option}
            isSelected={i === selectedIndex}
            aria-selected={i === selectedIndex ? 'true' : 'false'}
            aria-controls=''
            onClick={() => {
              setSelectedIndex(i);
            }}
            onKeyDown={(event) => {
              if ([' ', 'Enter'].includes(event.key)) setSelectedIndex(i);
            }}
            onFocus={() => {
              if (selectedIndex !== i) {
                setSelectedIndex(i);
              }
            }}
            size={size}
            isHueInfused={isHueInfused}
            responsive={responsive}
            themeSwitchMode={themeSwitchMode}
          />
        ))}
      </ul>
    </div>
  );
};

interface ToggleItemProps {
  value: string;
  isSelected: boolean;
  onClick: ButtonElement['onClick'];
  onKeyDown: ButtonElement['onKeyDown'];
  onFocus: ButtonElement['onFocus'];
  isHueInfused: ToggleProps['isHueInfused'];
  size: ToggleProps['size'];
  responsive: ToggleProps['responsive'];
  themeSwitchMode: ToggleProps['themeSwitchMode'];
}

const ToggleItem = ({
  value,
  onClick,
  onKeyDown,
  onFocus,
  isSelected,
  isHueInfused,
  size,
  responsive,
  themeSwitchMode,
}: ToggleItemProps) => {
  return (
    <li
      className={cn(
        'relative isolate rounded-3xl text-center transition-colors duration-300',
        !isSelected && 'hover:bg-neutral-N04',
        responsive &&
          'bg-neutral-N00 border-neutral-N04 border md:border-none md:bg-transparent',
        !responsive && 'border-none bg-transparent'
      )}
    >
      <Button
        type='button'
        role='tab'
        aria-selected='false'
        data-disabled={undefined}
        disabled={undefined}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        noStyles
        className={cn(
          'w-full font-serif',
          !isHueInfused &&
            isSelected &&
            themeSwitchMode === 'normal' &&
            'text-neutral-N00',
          !isHueInfused &&
            isSelected &&
            themeSwitchMode === 'alternate' &&
            'text-neutral-N00 dark:text-neutral-dark-N00',
          isHueInfused &&
            isSelected &&
            themeSwitchMode === 'alternate' &&
            'text-neutral-N99 dark:text-neutral-dark-N99',
          isSelected && 'cursor-default',
          size === 'm' && 'text-button-text-m px-6 py-2',
          size === 's' && 'text-button-text-s px-5 py-1 leading-5'
        )}
        classNames={{
          inner: 'whitespace-nowrap',
        }}
      >
        {value}
      </Button>
      {isSelected && (
        <motion.div
          className={cn(
            'absolute left-0 top-0 z-[-1] h-full w-full rounded-[20px]',
            isHueInfused &&
              isSelected &&
              themeSwitchMode === 'normal' &&
              'border-gradient bg-neutral-N00 before:border-gradient overflow-visible border-none before:rounded-3xl before:!p-0.5',
            !isHueInfused &&
              isSelected &&
              themeSwitchMode === 'alternate' &&
              'dark:border-gradient dark:bg-neutral-N00 dark:before:border-gradient overflow-visible before:rounded-3xl before:!p-0.5 dark:border-none',
            isHueInfused &&
              isSelected &&
              themeSwitchMode === 'alternate' &&
              'border-gradient bg-neutral-N00 before:border-gradient dark:bg-neutral-dark-N00 overflow-visible border-none before:rounded-3xl before:p-0.5 dark:before:p-0',
            !isHueInfused && isSelected && 'bg-neutral-N99'
          )}
          layoutId='indicator'
        />
      )}
    </li>
  );
};
